import DefaultAwardIcon from '@assets/icons/DefaultAwardIcon'
import { useDialog } from '@globals/dialog/useDialogStore'
import { myAwardTypes } from '@kudos/http-client'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DialogLoading from '@shared/dialog/DialogLoading'
import EngagementDialogWithMobileDrawer from '@shared/dialog/EngagementDialogWithMobileDrawer'
import { useTranslatedField } from '@shared/utils/useTranslatedField'
import { useQuery } from '@tanstack/react-query'
import { T } from '@transifex/react'
import { format } from 'date-fns'
import { FC } from 'react'
import { useShallow } from 'zustand/shallow'
import AwardTypePointsChip from '../sendAward/AwardTypePointsChip'
import NominateForm from './NominateForm'
import PastNominations from './PastNominations'
import { useSendNominationFormStore } from './useSendNominationFormStore'

export const SEND_NOMINATIONS_MODAL_KEY = 'sendNominationsModalKey'

export type SendNominationsDialogData = {
  nominationId: string
  isPinned?: boolean
}

const SendNominationsModal: FC = () => {
  const getTranslatedField = useTranslatedField()
  const { dialogData: data, isOpen } = useDialog<SendNominationsDialogData>(
    SEND_NOMINATIONS_MODAL_KEY,
  )
  const {
    data: nominationData,
    isFetching,
    isLoading,
  } = useQuery({
    queryKey: ['getNominationQueryKey', data?.nominationId],
    queryFn: () =>
      myAwardTypes({
        embeds: ['attachmentUrls', 'ownNominees', 'questions'],
        idIn: [data?.nominationId ?? ''],
        page: 1,
        pageSize: 1,
      }),
    select: data => data.results[0],
    enabled: isOpen && !!data?.nominationId,
  })
  const { isFormDirty } = useSendNominationFormStore(
    useShallow(state => ({
      isFormDirty: state.isFormDirty,
    })),
  )

  if (!nominationData) return null

  const translatedName = getTranslatedField(nominationData, 'name')
  const translatedDescription = getTranslatedField(nominationData, 'description')

  return (
    <EngagementDialogWithMobileDrawer
      dialogKey={SEND_NOMINATIONS_MODAL_KEY}
      isFormDirty={isFormDirty}
      avatarUrls={nominationData.attachmentUrls}
      defaultIcon={<DefaultAwardIcon />}
      subtitle={
        nominationData.nominationEndAt && (
          <T
            _str="Nominations close {date}"
            date={format(nominationData.nominationEndAt, 'PPPp')}
          />
        )
      }
      chip={
        <AwardTypePointsChip
          points={nominationData.points}
          pointsLocked={nominationData.pointsLocked}
          isNomination
        />
      }
      title={translatedName}>
      {isFetching || isLoading ? (
        <DialogLoading />
      ) : (
        <>
          <Stack spacing={2}>
            <Typography variant="body1">{translatedDescription}</Typography>
            <PastNominations
              ownNominees={nominationData.ownNominees}
              nominationId={nominationData.id}
            />
          </Stack>
          <NominateForm nomination={nominationData} isPinned={data?.isPinned} />
        </>
      )}
    </EngagementDialogWithMobileDrawer>
  )
}

export default SendNominationsModal
